(() => {
  if (window.SBAnalyticsConsent) return;

  const subscribers = new Set();
  const checkoutPreparers = new Set();
  const pendingCheckouts = new WeakSet();
  const resumedCheckouts = new WeakSet();
  const checkoutAttributionTimeout = 1000;
  let loadInFlight = false;
  let retryTimer = 0;
  let retryDelay = 50;

  const apiAvailable = () => (
    typeof window.Shopify?.customerPrivacy?.analyticsProcessingAllowed === 'function'
  );

  const analyticsAllowed = () => {
    if (!apiAvailable()) return null;
    try {
      return Boolean(window.Shopify.customerPrivacy.analyticsProcessingAllowed());
    } catch (_) {
      return false;
    }
  };

  const notify = () => {
    for (const subscriber of subscribers) {
      try {
        subscriber();
      } catch (_) {}
    }
  };

  const scheduleRetry = () => {
    if (retryTimer) return;
    retryTimer = window.setTimeout(() => {
      retryTimer = 0;
      ensureLoaded();
    }, retryDelay);
    retryDelay = Math.min(retryDelay * 2, 2000);
  };

  const ensureLoaded = () => {
    if (apiAvailable()) {
      queueMicrotask(notify);
      return;
    }
    if (loadInFlight) return;
    if (typeof window.Shopify?.loadFeatures !== 'function') {
      scheduleRetry();
      return;
    }

    loadInFlight = true;
    window.Shopify.loadFeatures(
      [{ name: 'consent-tracking-api', version: '0.1' }],
      (error) => {
        loadInFlight = false;
        if (error || !apiAvailable()) {
          scheduleRetry();
          return;
        }
        retryDelay = 50;
        notify();
      },
    );
  };

  const whenReady = (subscriber) => {
    if (typeof subscriber !== 'function') return () => {};
    subscribers.add(subscriber);
    ensureLoaded();
    return () => subscribers.delete(subscriber);
  };

  const onCheckout = (prepare) => {
    checkoutPreparers.add(prepare);
    return () => checkoutPreparers.delete(prepare);
  };

  document.addEventListener('submit', (event) => {
    const form = event.target;
    const submitter = event.submitter;
    if (event.defaultPrevented || !submitter?.matches('[data-cart-checkout-button]') || resumedCheckouts.has(form)) return;
    const block = () => {
      event.preventDefault();
      event.stopImmediatePropagation();
    };
    if (pendingCheckouts.has(form)) {
      block();
      return;
    }

    const pending = [];
    for (const prepare of checkoutPreparers) {
      // Attribution is optional, including a synchronous preparation failure.
      try {
        const result = prepare();
        if (result) pending.push(result);
      } catch (_) {}
    }
    if (!pending.length) return;
    block();
    pendingCheckouts.add(form);
    let resumed = false;
    const resume = () => {
      if (resumed) return;
      resumed = true;
      clearTimeout(timer);
      pendingCheckouts.delete(form);
      if (!form.isConnected || submitter.form !== form) return;
      // Bypass every tracker during this one synchronous submission. Keep the
      // normal form/submitter path so the cart's discount handler still runs.
      resumedCheckouts.add(form);
      try { form.requestSubmit(submitter); }
      finally { resumedCheckouts.delete(form); }
    };
    const timer = setTimeout(resume, checkoutAttributionTimeout);
    void Promise.allSettled(pending).then(resume);
  }, true);

  document.addEventListener('shopifyCustomerPrivacyApiLoaded', () => {
    if (apiAvailable()) notify();
    else ensureLoaded();
  });
  document.addEventListener('visitorConsentCollected', notify);

  window.SBAnalyticsConsent = Object.freeze({ analyticsAllowed, whenReady, onCheckout });
  ensureLoaded();
})();
